/**
 * @file 教师新建 / 编辑弹窗
 * @description 名称、账号（仅新建）、科目（多选）、密码（仅新建，可空默认 123456）。
 *
 * @whenToUse 教师管理页创建与编辑。
 * @whenNotToUse 其他业务表单弹窗。
 */

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { Input } from '@/components/ui/input.tsx'
import { MultiSelect } from '@/components/ui/multi-select.tsx'
import type { TeacherItem } from '@/api/teacher.ts'
import {
  EMPTY_TEACHER_FORM,
  SUBJECT_OPTIONS,
  type TeacherFormValues,
} from '@/components/pages/teacher-page/types.ts'

/** 登录账号：字母 / 数字 / 下划线 */
const ACCOUNT_PATTERN = /^[a-zA-Z0-9_]+$/

/**
 * 过滤账号非法字符
 * @param value 原始输入
 */
function sanitizeAccount(value: string): string {
  return value.replace(/[^a-zA-Z0-9_]/g, '')
}

export type TeacherFormDialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
  /** 编辑目标；为空表示新建 */
  editing: TeacherItem | null
  /** 提交（编辑目标由 editing 提供，无需 id） */
  onSubmit: (values: TeacherFormValues) => Promise<void>
}

/**
 * 教师创建 / 编辑 Dialog
 */
export function TeacherFormDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
}: TeacherFormDialogProps) {
  const isEdit = Boolean(editing)
  const [values, setValues] = useState<TeacherFormValues>(EMPTY_TEACHER_FORM)
  const [errors, setErrors] = useState<
    Partial<Record<keyof TeacherFormValues, string>>
  >({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    if (editing) {
      setValues({
        teacherName: editing.teacherName,
        userName: editing.userName,
        subjects: editing.subjects ? editing.subjects.split(',') : [],
        password: '',
      })
    } else {
      setValues(EMPTY_TEACHER_FORM)
    }
    setErrors({})
  }, [open, editing])

  const setField = <K extends keyof TeacherFormValues>(
    key: K,
    value: TeacherFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const validate = () => {
    const next: Partial<Record<keyof TeacherFormValues, string>> = {}
    if (!values.teacherName.trim()) next.teacherName = '请输入教师名称'
    const account = values.userName.trim()
    if (!isEdit) {
      if (!account) next.userName = '请输入教师账号'
      else if (!ACCOUNT_PATTERN.test(account))
        next.userName = '仅支持字母、数字和下划线'
    }
    if (values.subjects.length === 0) next.subjects = '请选择科目'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async () => {
    if (!validate() || submitting) return
    setSubmitting(true)
    try {
      await onSubmit(values)
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? '编辑教师' : '新增教师'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button loading={submitting} onClick={handleSubmit}>
            {isEdit ? '保存' : '创建'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="教师名称" required error={errors.teacherName}>
          <Input
            value={values.teacherName}
            invalid={Boolean(errors.teacherName)}
            placeholder="请输入教师名称"
            onChange={(event) => setField('teacherName', event.target.value)}
          />
        </FormField>
        <FormField
          label="教师账号"
          required
          error={errors.userName}
          hint="仅支持字母、数字和下划线"
        >
          <Input
            value={values.userName}
            invalid={Boolean(errors.userName)}
            placeholder="如 wang_teacher"
            autoComplete="username"
            disabled={isEdit}
            onChange={(event) =>
              setField('userName', sanitizeAccount(event.target.value))
            }
          />
        </FormField>
        <FormField label="科目" required error={errors.subjects}>
          <MultiSelect
            value={values.subjects}
            options={SUBJECT_OPTIONS}
            placeholder="请选择科目（可多选）"
            maxTagCount={3}
            invalid={Boolean(errors.subjects)}
            onChange={(value) => setField('subjects', value)}
          />
        </FormField>
        {!isEdit ? (
          <FormField
            label="教师密码"
            error={errors.password}
            hint="非必填，留空则默认 123456"
          >
            <Input
              type="password"
              value={values.password}
              invalid={Boolean(errors.password)}
              placeholder="请输入密码（默认123456）"
              autoComplete="new-password"
              onChange={(event) => setField('password', event.target.value)}
            />
          </FormField>
        ) : null}
      </div>
    </Dialog>
  )
}
